import trimEnd from 'lodash-es/trimEnd'
import fetch from 'node-fetch'

import {
  ILogger, IBTCUnspent,
  NetworkError, RPCError, RPCNode, ChainType,
} from '@one-chain/core'

import { toSat } from '../helper'
import { ISlowApiProvider } from './interface'

const CHAIN_TYPE_MAP = {
  [ChainType.BTC]: 'BTC',
}

const CHAIN_ID_MAP = {
  'mainnet': '',
  'testnet': 'TEST',
}

export class ChainSo implements ISlowApiProvider {
  protected logger: ILogger
  protected endpoint: string
  protected network: string // Network here is an api paramter

  readonly supportBatch = false
  readonly rateLimit = 5

  static instance: ChainSo

  constructor (rpcnode: RPCNode, logger: ILogger) {
    if (ChainSo.instance) {
      return ChainSo.instance
    }

    this.logger = logger
    this.endpoint = trimEnd(rpcnode.baseUrl, '/')
    this.network = CHAIN_TYPE_MAP[rpcnode.chainType] + CHAIN_ID_MAP[rpcnode.chainId]

    ChainSo.instance = this
  }

  async ping () {
    return this.get('/get_info/{network}/')
  }

  async getAddress (address: string) {
    const ret = await this.get(`/address/{network}/${address}`)
    return {
      address: ret.data.address,
      balance: toSat(ret.data.balance).toNumber(),
      txCount: ret.data.total_txs,
    }
  }

  async getUnspentOfAddress (address: string, onlyConfirmed = false) {
    const result = await this.get(`/get_tx_unspent/{network}/${address}`)

    const unspents: IBTCUnspent[] = []
    for (const tx of result.data.txs) {
      // Skip unconfirmed unspent if only choose confirmed unspent.
      if (onlyConfirmed && tx.confirmations <= 6) {
        continue
      }

      unspents.push({
        txId: tx.txid,
        address,
        vout: tx.output_no,
        value: toSat(tx.value).toNumber(),
      })
    }

    return unspents
  }

  async pushTransaction (rawTransaction: string): Promise<string> {
    const ret = await this.post('/send_tx/{network}', { tx_hex: rawTransaction })
    return ret.data.txid
  }

  protected buildUrl (path: string, query: any = null) {
    let url = this.endpoint + path.replace('{network}', this.network)

    if (query) {
      url += '?' + new URLSearchParams(query)
    }

    return url
  }

  protected async get (path, query: any = null): Promise<any> {
    let ret
    try {
      const url = this.buildUrl(path, query)
      !PROD && this.logger.trace('BTC - Request data: ', url)
      const res = await fetch(url, {
        method: 'GET',
        // @ts-ignore
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
        },
      })

      if (res.ok) {
        ret = await res.text()
        if (ret) {
          ret = JSON.parse(ret)
        }
        !PROD && this.logger.trace('BTC - Response data: ', ret)

        if (ret.status !== 'success') {
          throw new RPCError(ret.code, ret.message, ret)
        }
      }
      else {
        throw NetworkError.fromCode(res.status, `${res.status} ${res.statusText}`)
      }
    }
    catch (err) {
      if (err instanceof NetworkError) {
        throw err
      }
      else {
        throw NetworkError.fromCode(0, err.message)
      }
    }

    return ret
  }

  protected async post (path, data, query: any = null): Promise<any> {
    let ret
    try {
      const url = this.buildUrl(path, query)
      !PROD && this.logger.trace('BTC - Request data: ', url, data)
      const res = await fetch(url, {
        method: 'POST',
        // @ts-ignore
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })

      if (res.ok) {
        ret = await res.text()
        if (ret) {
          ret = JSON.parse(ret)
        }
        !PROD && this.logger.trace('BTC - Response data: ', ret)

        if (ret.status !== 'success') {
          throw new RPCError(ret.code, ret.message, ret)
        }
      }
      else {
        throw NetworkError.fromCode(res.status, `${res.status} ${res.statusText}`)
      }
    }
    catch (err) {
      if (err instanceof NetworkError) {
        throw err
      }
      else {
        throw NetworkError.fromCode(0, err.message)
      }
    }

    return ret
  }
}

export default ChainSo
